// Generate all 15-minute intervals in a day (96 slots total)
// Params: none
// Output: ["00:00", "00:15", ..., "23:45"]
export const timeIntervals = Array.from({ length: 96 }, (_, i) => {
    const totalMinutes = i * 15;
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    const minutes = String(totalMinutes % 60).padStart(2, "0");
    return `${hours}:${minutes}`;
});

// Get the current datetime in YYYY-MM-DD HH:MM:SS format
// Params: none
// Output: "2025-09-05 17:32:45"
export const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Get the current time in HH:MM format
// Params: none
// Output: "14:07"
export const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
};

// Get the current date in YYYY-MM-DD format
// Params: none
// Output: "2025-08-19"
export const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// Get the next nearest 15-minute interval from now
// Params: none
// Output: "14:15" (if current time is 14:07)
export const getNextAvailable15MinTime = () => {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();

    minutes = Math.ceil(minutes / 15) * 15;

    if (minutes === 60) {
        minutes = 0;
        hours = (hours + 1) % 24;
    }

    const hourStr = String(hours).padStart(2, "0");
    const minuteStr = String(minutes).padStart(2, "0");
    return `${hourStr}:${minuteStr}`;
};

// Get the next nearest 15-minute interval based on an added duration (in hours)
// Params: duration (number, default=0) → hours to add
// Example: getNextAvailable15MinTimeBasedOnDuration(2)
// Output: "16:30" (if current time is 14:07)
export const getNextAvailable15MinTimeBasedOnDuration = (duration = 0) => {
    const now = new Date();
    now.setHours(now.getHours() + duration);

    let hours = now.getHours();
    let minutes = now.getMinutes();

    minutes = Math.ceil(minutes / 15) * 15;

    if (minutes === 60) {
        minutes = 0;
        hours = (hours + 1) % 24;
    }

    const hourStr = String(hours).padStart(2, "0");
    const minuteStr = String(minutes).padStart(2, "0");
    return `${hourStr}:${minuteStr}`;
};

// Get human-readable time difference between two times
// Params: start (string "HH:MM"), end (string "HH:MM")
// Example: getReadableTimeDiff("13:00", "14:45")
// Output: "1 hour 45 minutes"
export const getReadableTimeDiff = (start, end) => {
    const [startHour, startMin] = start.split(":").map(Number);
    const [endHour, endMin] = end.split(":").map(Number);

    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;

    let diff = endTotal - startTotal;
    if (diff < 0) diff += 24 * 60; // wrap around midnight

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);

    return parts.length ? parts.join(" ") : "0 minutes";
};

// Format a date object {year, month, day} into YYYY-MM-DD
// Params: {year: 2025, month: 8, day: 19}
// Output: "2025-08-19"
export const formatDateToYMD = (dateObj) => {
    return `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`;
}

// Format a date string into readable format
// Params: "2025-08-19"
// Output: "August 19, 2025"
export const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
}

// Check if a meeting is currently active
// Params: _meets_date (YYYY-MM-DD), _start_time (HH:MM), _end_time (HH:MM)
// Output: true/false
export const isMeetingActive = (_meets_date, _start_time, _end_time) => {
    const now = new Date();
    const start = new Date(`${_meets_date}T${_start_time}`);
    const end = new Date(`${_meets_date}T${_end_time}`);
    return now >= start && now <= end;
}

// Check if a meeting has already ended
// Params: _meets_date (YYYY-MM-DD), _end_time (HH:MM)
// Output: true/false
export const isMeetingPast = (_meets_date, _end_time) => {
    const now = new Date();
    const end = new Date(`${_meets_date}T${_end_time}`);
    return now > end;
};

// Check if a meeting has not started yet
// Params: _meets_date (YYYY-MM-DD), _start_time (HH:MM)
// Output: true/false
export const isMeetingUpcoming = (_meets_date, _start_time) => {
    const now = new Date();
    const start = new Date(`${_meets_date}T${_start_time}`);
    return now < start;
};

// Get short month name from a YYYY-MM-DD string
// Params: _date_str = "2025-08-19"
// Output: "Aug"
export const getShortMonthName = (_date_str) => {
    const date = new Date(_date_str);
    return date.toLocaleString("en-US", { month: "short" });
}

// Get short month name from a YYYY-MM-DD string
// Params: _date_str = "2025-08-19"
// Output: ""Tuesday, 19 Aug 2025"
export const formatHumanDate = (_date_str) => {
    const date = new Date(_date_str);
    return date.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}


export const isTimePassed = (timeStr) => {
    // timeStr expected as "HH:MM"
    const [hours, minutes] = timeStr.split(":").map(Number);

    // Create a Date for today with the provided time
    const now = new Date();
    const target = new Date();
    target.setHours(hours, minutes, 0, 0);

    return now >= target;
}